import React, { useCallback, useEffect, useRef } from 'react'
import List from '@mui/material/List'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import { useIsMobileLayout } from '../../../utils/hooks/useIsMobileLayout'
import { useLocation } from 'react-router-dom'
import {
  AdGroupStatus,
  CompanyType,
  Status,
  useSidebarAdvertiserLazyQuery,
  useSidebarPublisherLazyQuery,
  useSidebarTradingDeskLazyQuery,
} from '../../../__generated__/graphql'
import { SidebarHeader } from './SidebarHeader'
import { SidebarFooter } from './SidebarFooter'
import { SidebarBody } from './SidebarBody'
import { CompanyDetailsContextProvider, useCompanyDetailsContext } from '../../../utils/hooks/useCompanyDetailsContext'
import { styled } from '@mui/material'
import SidebarSkeleton from '../../loader/SidebarSkeleton'
import AgencySidebarContent from './AgencySidebar'
import { fetchAllPages } from '../../../utils/fetchAllPages'
import { useFetchAllPages } from '../../../utils/hooks/useFetchAllPages'
import { SIDEBAR_QUERY_PAGE_SIZE } from './sidebarPagination'
import { sidebarPalette, sidebarContentSx } from './sidebarStyles'
import { useRegisterSidebarReload, useSidebarContext } from '../../../utils/providers/sidebarContext'

const DivSidebar = styled('div')(({ theme }) => ({
  backgroundColor: sidebarPalette.main,
  width: theme.layout.sidebarWidth,
  height: '100%',
  display: 'flex',
  overflow: 'hidden',
  ...sidebarContentSx,

  '& hr': {
    borderColor: sidebarPalette.separator,
  },
}))

export const ListSidebarContent = styled(List)(() => ({
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  height: '100%',
  paddingTop: 0,
  paddingBottom: 0,
  overflow: 'hidden',
  wordWrap: 'break-word',
  ...sidebarContentSx,
}))

const Sidebar: React.FC<{ companySlug: string }> = ({ companySlug }) => {
  const isMobile = useIsMobileLayout()
  const location = useLocation()
  const { isOpen, close } = useSidebarContext()
  const previousPathname = useRef(location.pathname)

  useEffect(() => {
    if (!isMobile || previousPathname.current === location.pathname) return

    close()
    previousPathname.current = location.pathname
  }, [close, isMobile, location.pathname])

  const content = (
    <CompanyDetailsContextProvider companySlug={companySlug}>
      <SidebarContentType />
    </CompanyDetailsContextProvider>
  )

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        data-testid="sidebarMobileDrawer"
        onClose={close}
        open={isOpen}
        slotProps={{
          paper: {
            sx: {
              width: '100%',
              backgroundColor: sidebarPalette.main,
              ...sidebarContentSx,
            },
          },
        }}
        variant="temporary"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>{content}</Box>
      </Drawer>
    )
  }

  return <DivSidebar>{content}</DivSidebar>
}

const SidebarContentType: React.FC = () => {
  const {
    company: { type },
  } = useCompanyDetailsContext()

  if (type === CompanyType.Agency) return <AgencySidebarContent />
  else return <SidebarContent />
}

export interface Item {
  id: string
  name: string
  status: Status
  subItems: Array<{
    id: string
    name: string
    status?: Status | AdGroupStatus
  }>
}

const SidebarContent = (): JSX.Element => {
  const companyDetails = useCompanyDetailsContext()
  const [hasArchived, setHasArchived] = React.useState(false)

  const [loadAdvertiserQuery] = useSidebarAdvertiserLazyQuery({
    notifyOnNetworkStatusChange: true,
  })
  const [loadTradingDeskQuery] = useSidebarTradingDeskLazyQuery({
    notifyOnNetworkStatusChange: true,
  })
  const [loadPublisherQuery] = useSidebarPublisherLazyQuery({
    notifyOnNetworkStatusChange: true,
  })

  const loadAll = useCallback(async (): Promise<Item[]> => {
    const status = Object.values(Status).filter((status) => hasArchived || status !== Status.Archived)

    if (companyDetails.company.type === CompanyType.Advertiser) {
      const adGroupStatus = Object.values(AdGroupStatus).filter(
        (status) => hasArchived || status !== AdGroupStatus.Archived
      )

      return fetchAllPages(async (pagination) => {
        const { data } = await loadAdvertiserQuery({
          variables: {
            companyId: companyDetails.company.id,
            campaignStatus: status,
            adGroupStatus,
            pagination,
          },
        })

        return data?.searchCampaigns ?? []
      }, SIDEBAR_QUERY_PAGE_SIZE)
    }

    if (companyDetails.company.type === CompanyType.TradingDesk) {
      const adGroupStatus = Object.values(AdGroupStatus).filter(
        (status) => hasArchived || status !== AdGroupStatus.Archived
      )

      return fetchAllPages(async (pagination) => {
        const { data } = await loadTradingDeskQuery({
          variables: {
            companyId: companyDetails.company.id,
            campaignStatus: status,
            adGroupStatus,
            pagination,
          },
        })

        return data?.searchProgrammaticCampaigns ?? []
      }, SIDEBAR_QUERY_PAGE_SIZE)
    }

    return fetchAllPages(async (pagination) => {
      const { data } = await loadPublisherQuery({
        variables: {
          companyId: companyDetails.company.id,
          status,
          pagination,
        },
      })

      return data?.searchWebsites ?? []
    }, SIDEBAR_QUERY_PAGE_SIZE)
  }, [
    companyDetails.company.id,
    companyDetails.company.type,
    hasArchived,
    loadAdvertiserQuery,
    loadPublisherQuery,
    loadTradingDeskQuery,
  ])

  const { items, loading, reload } = useFetchAllPages(loadAll)
  useRegisterSidebarReload(reload)

  return (
    <ListSidebarContent>
      <SidebarHeader updateWithArchived={(showArchived: boolean) => setHasArchived(showArchived)} items={items} />

      {loading && items.length === 0 ? (
        <SidebarSkeleton />
      ) : (
        <SidebarBody items={items} updateSidebarItems={() => reload()} />
      )}

      <SidebarFooter />
    </ListSidebarContent>
  )
}

export default Sidebar
